import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { resolveDisplayableWebsiteUrl } from '../../utils/displayableWebsiteUrl';
import AddAssociateModal from '../auth/AddAssociateModal';
import AllReviewsList from '../customers/AllReviewsList';
import { StarRating } from '../profile/customersurveyform';
import DOMPurify from 'dompurify';
import { useProfileStore } from '../../state/profileStore';


function decodeEntities(encodedString) {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = encodedString;
  return textarea.value;
}

const CompanyProfile = () => {
  // Two route patterns map to this component:
  //   (a) /company/by-id/:id   — internal "my own company" navigation. Stable
  //       because b_id is an integer, no URL-parseability concerns. Required
  //       for GBP / long-tail signups whose b_website stores a platform URL
  //       (e.g., https://www.google.com/maps/place/?q=place_id:CHIJ...) that
  //       the splat route below would mangle into the wrong lookup.
  //   (b) /company/*           — public/legacy path that looks up by website
  //       LIKE-substring. Still in use for franchise URLs and external links.
  // Whichever param is present drives the fetch below.
  const params = useParams();
  const companyById = params.id || null;
  const website = params['*']; // splat captures full path for franchise URLs
  const [company, setCompany] = useState(null);
  const [users, setUsers] = useState([]);
  const [businessTooltip, setBusinessTooltip] = useState('Loading...');
  const [isAddAssociateModalOpen, setIsAddAssociateModalOpen] = useState(false);
  const navigate = useNavigate();
  // Individual selectors (see userprofile.jsx for context) — whole-store
  // destructure caused 429-rate-limit loops on /rating-summary after the
  // Teams focus modal save 2026-05-26.
  const profile = useProfileStore((s) => s.profile);
  const refreshProfile = useProfileStore((s) => s.refreshProfile);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoaded, setReviewsLoaded] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [visibleCount, setVisibleCount] = useState(10); // how many reviews to render
  const sentinelRef = useRef(null);                     // sentinel div for IntersectionObserver
  const isFetchingRef = useRef(false); // Prevents double triggers

  const [ratingData, setRatingData] = useState({
    u_total_review_score: 0,
    u_review_count: 0,
    u_avg_rating: null
  });

    // Company-level rating from merged reviews (all associates + Google)
  const [companyRating, setCompanyRating] = useState({ avg: null, count: 0 });

  // Memoized, sanitized About-Us HTML from the company payload (prevents flicker)
  const aboutUsHTML = useMemo(() => {
    if (!company?.b_about_us) return null;
    const decoded = decodeEntities(company.b_about_us);
    return DOMPurify.sanitize(decoded);
  }, [company?.b_about_us]);

  // New: Memoized color map to assign unique colors sequentially based on sorted user IDs
  const colorMap = useMemo(() => {


  const userIds = [...new Set(users.map(u => u.u_id))]; // Unique IDs, in case of duplicates
  userIds.sort((a, b) => a - b); // Sort ascending for deterministic order

  const colors = [
    '#FF0505', // Red
    '#48BB78', // Green
    '#8cc3f0', // Blue
    '#fff93f', // Yellow 
    '#9F7AEA', // Purple
    '#ED8936', // Orange
    '#fabcbc', // Pink-red
    '#002fff'  // Indigo
  ];

  const map = new Map();
  userIds.forEach((uid, index) => {
    map.set(uid, colors[index % colors.length]);
  });

  return map;
}, [users]);

// Updated: Now uses the colorMap for lookup
const getUserColor = (userId) => {
  if (!userId) return '#CCCCCC'; // Neutral for unknown
  return colorMap.get(userId) || '#CCCCCC';
};
// Extract a numeric rating from any review shape we receive
const getReviewScore = (r) => {
  const val =
    r.rating ??
    r.customersurvey_google_score ??
    r.customersurvey_score ??
    r.score ??
    r.customersurvey_rating ??
    null;
  if (val == null) return null;
  const n = typeof val === 'string' ? parseFloat(val) : val;
  return Number.isFinite(n) ? n : null;
};

  const generateProfileSlug = (firstName, lastName) => {
    if (!firstName || !lastName) return '';
    return `${firstName.toLowerCase()}_${lastName.toLowerCase()}`;
  };
  const { slug } = useParams();
  const fetchRatingSummary = async (userId) => {
    try {
      const response = await axios.get(
        `/api/profile/${userId}/rating-summary`,
        { withCredentials: true }
      );
      setRatingData({
        u_total_review_score: parseFloat(response.data.u_total_review_score),
        u_review_count: response.data.u_review_count,
        u_avg_rating: parseFloat(response.data.u_avg_rating)
      });
    } catch (err) {
      console.error('Failed to load ratings:', err);
    }
  };

  const fetchReviews = async (userId) => {
    try {
      const res = await axios.get(`/api/profile/${userId}/reviews`);
      setReviews(res.data);
    } catch (err) {
      console.error('Review fetch failed:', err);
      setReviews([]);
    }
  };

useEffect(() => {
  const fetchData = async () => {
    // Both lookup paths share the same downstream pipeline (users +
    // merged reviews keyed on b_id). Only the company-fetch URL differs.
    const endpoint = companyById
      ? `/api/company/by-id/${encodeURIComponent(companyById)}`
      : (website ? `/api/company/${encodeURIComponent(website)}` : null);
    if (!endpoint) return;

    try {
      // 1. Fetch company data
      const companyResponse = await axios.get(endpoint, { withCredentials: true });
      setCompany(companyResponse.data);

      // 2. Fetch all users for this company
      const usersResponse = await axios.get(
        `/api/business/${companyResponse.data.b_id}/users`,
        { withCredentials: true }
      );
      setUsers(usersResponse.data);

      // 3. Fetch merged reviews (all eHandshake from all users + owner's 5 Google, sorted desc)
      const reviewsResponse = await axios.get(
        `/api/profile/business/${companyResponse.data.b_id}/reviews-merged`,
        { withCredentials: true }
      );
      const mergedReviews = reviewsResponse.data;

      // Update the company-level rating using the merged list
      const scores = mergedReviews.map(getReviewScore).filter((s) => s != null);
      const count = scores.length;
      const avg = count ? Math.round((scores.reduce((a, b) => a + b, 0) / count) * 10) / 10 : null;
      setCompanyRating({ avg, count });

      setReviews(mergedReviews);
      setReviewsLoaded(true);

    } catch (error) {
      console.error('Error fetching data:', error);
      setReviewsLoaded(true);
    }
  };

  fetchData();
}, [website, companyById]);

  useEffect(() => {
    const uid = profile?.u_id;
    if (!uid) return;

    (async () => {
      // keep the store fresh once the id is known
      await refreshProfile(uid);
      // then load the rating summary for header/badges
      fetchRatingSummary(uid);
    })();
  }, [profile?.u_id]);



  useEffect(() => {
    const fetchTooltips = async () => {
      if (!company?.b_customer_focus) {
        console.log('No company focus available for tooltips');
        return;
      }

      try {
        const businessResponse = await axios.get(
          `/api/tooltips/business/${company.b_customer_focus}?warning=false`
        );
        setBusinessTooltip(`${company.b_name} ${businessResponse.data?.t_template || ''}`);

        if (users.length > 0) {
          const updatedUsers = await Promise.all(users.map(async (associate) => {
            if (!associate.u_customer_focus) return associate;
            
            const userResponse = await axios.get(
              `/api/tooltips/associate/${associate.u_customer_focus}?warning=false`
            );
            
            return {
              ...associate,
              tooltip_text: `${associate.u_first_name} represents ${company.b_name}, and ${userResponse.data?.t_template || ''}`
            };
          }));
          
          setUsers(updatedUsers);
        }
      } catch (error) {
        console.error('Tooltip fetch error:', error);
      }
    };

    fetchTooltips();
  }, [company, users]);

  const handleUserClick = (associate) => {
    // Use the id-based route (/profile/id/:id) when we have a u_id —
    // avoids the Thai-slug URL-encoding hitches Brett caught 2026-05-26
    // (a 200-byte percent-encoded URL freezes Chrome's address bar). The
    // slug-based route still exists for public/shared links where only the
    // slug is known.
    if (associate?.u_id) {
      navigate(`/profile/id/${associate.u_id}`);
      return;
    }
    navigate(`/profile/${generateProfileSlug(associate.u_first_name, associate.u_last_name)}`);
  };
  // Infinite scroll: observe the sentinel and extend the visible window
  const handleReviewsScroll = (e) => {
    const el = e.currentTarget;
    if (!el) return;

    // Early trigger to start loading around ~7 reviews worth of scroll (tune if needed)
    const earlyTriggerPx = 900;

    const scrolledPastEarlyPoint = el.scrollTop >= earlyTriggerPx;
    const nearBottom =
      el.scrollTop + el.clientHeight >= el.scrollHeight - earlyTriggerPx;

    if (
      (scrolledPastEarlyPoint || nearBottom) &&
      !isFetchingRef.current &&
      visibleCount < reviews.length
    ) {
      isFetchingRef.current = true;

      setVisibleCount((prev) => {
        const remaining = reviews.length - prev;
        return prev + Math.min(10, remaining);
      });

      setTimeout(() => {
        isFetchingRef.current = false;
      }, 200);
    }
  };




  if (!company) {
    return <div></div>;
  }


  return (
    <div className="container mx-auto w-4/5 pt-6 flex flex-col min-h-screen">
      <div className="flex flex-col md:flex-row gap-3 flex-grow">
        {/* Left Column - Business info */}
        <div className="w-full md:w-[48%] bg-white h-[calc(100vh-7rem)] rounded-lg p-4 shadow-[0_0_20px_rgba(0,0,150,0.1)] flex flex-col">

          {/* === BUSINESS HEADER CARD: Name, Contact, Logo, Industry === */}
          <div
            className="rounded-lg p-3 mb-3 bg-white"
            style={{
              boxShadow: '0 2px 6px -1px rgba(0, 174, 247, 0.12), 0 1px 2px -1px rgba(0, 174, 247, 0.08), inset 0 -4px 6px -4px rgba(0, 174, 247, 0.05)',
              border: '1px solid rgba(0, 174, 247, 0.15)',
              borderBottomColor: 'rgba(0, 174, 247, 0.2)',
              borderBottomWidth: '2px'
            }}
          >
            {/* Business Name */}
            <div className="w-full mb-0">
              <h1 className="text-2xl font-bold break-words text-[#00aef7] leading-tight">
                {company.b_name}
              </h1>
            </div>

            {/* Contact Info and Logo Section */}
            <div className="flex justify-between items-start">
              <div className="flex-1 pt-1.5">
                {company.b_show_address ? (
                  <div style={{ minHeight: '60px' }}>
                    <p className="text-sm text-gray-600 leading-none">
                      {company.b_address || 'Street Address'}
                    </p>
                    <p className="text-sm text-gray-600 leading-normal mb-3">
                      {(company.b_city || company.b_state || company.b_zip_code)
                        ? `${company.b_city || ''}, ${company.b_state || ''} ${company.b_zip_code || ''}`
                        : 'City, State Zip Code'}
                    </p>
                  </div>
                ) : null}

                <p className="text-sm text-gray-600 leading-none">
                  <a href={`tel:${company.b_phone || ''}`} className="hover:text-blue-600">
                    {company.b_phone || 'Business Phone'}
                  </a>
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  <a
                    href={resolveDisplayableWebsiteUrl(company?.b_website, company?.b_place_id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#00aef7] hover:text-blue-600"
                  >
                    Visit our Website
                  </a>
                </p>
              </div>

              {/* Right Side - Logo and Badge */}
              <div className="w-[54%] relative pt-4">
                <div className="relative w-full h-24 max-w-[280px] mx-auto group">
                  {company.b_logo ? (
                    <img
                      src={`${process.env.REACT_APP_API_URL}${company.b_logo}`}
                      alt="Business Logo"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <img
                      src="/images/Logo_Placeholder.jpg"
                      alt="Logo Placeholder"
                      className="w-full h-full object-contain opacity-50 border-2 border-[#00aef7]/30 rounded-md"
                    />
                  )}
                </div>

                {/* Industry and Rating */}
                <div className="mt-1 text-center">
                  <p className="text-lg font-bold text-gray-600">
                    {company.b_business_industry}
                  </p>
                  {companyRating.count > 0 && (
                    <div className="mt-1 flex items-center justify-center gap-2">
                      <StarRating rating={companyRating.avg} />
                      <span className="text-sm font-semibold text-[#00aef7]">
                        {companyRating.avg.toFixed(1)} ({companyRating.count} reviews)
                      </span>
                    </div>
                  )}
                </div>

                {/* Badge — Slice 3: hide entirely when b_customer_focus is unset. */}
                {company.b_customer_focus?.trim() && (
                  <div className="absolute -top-[12px] -right-1 z-20">
                    <div className="relative group">
                      <div className="text-sm font-bold bg-[#00aef7]/10 px-2 py-0.5 rounded-full border border-[#00aef7]/20 text-[#00aef7] cursor-help">
                        {company.b_customer_focus}
                      </div>
                      <div className="hidden group-hover:block absolute z-20 bg-black text-white text-xs p-2 rounded-md w-64 -translate-x-1/2 left-1/2 mt-6">
                        {businessTooltip}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* === ABOUT US SECTION === */}
          {aboutUsHTML ? (
            <div
              className="flex-1 min-h-0 overflow-y-auto rounded-lg pl-3 pr-2 scrollbar-thin scrollbar-thumb-gray-100 scrollbar-track-transparent"
              style={{
                backgroundColor: '#ffffff',
                boxShadow: 'inset 0 2px 8px -2px rgba(0, 0, 0, 0.08), inset 0 0 0 1px rgba(0, 0, 0, 0.04)',
                border: '1px solid rgba(0, 0, 0, 0.06)'
              }}
            >
              <div
                className="p-4 text-gray-700"
                dangerouslySetInnerHTML={{ __html: aboutUsHTML }}
              />
            </div>
          ) : null}

        </div>

{/* Middle Column - Associates */}
<div className="w-full md:w-[24%] bg-white rounded-lg p-4 h-[calc(100vh-7rem)] shadow-[0_0_20px_rgba(0,0,150,0.1)] flex flex-col">
  <h2 className="text-xl font-bold text-[#00aef7] mb-2 text-center sticky top-0 bg-white z-10">
    Associates
  </h2>
  <div className="overflow-y-auto max-h-[500px] pr-0 scrollbar-thin scrollbar-thumb-gray-100 scrollbar-track-transparent">
    <div className="grid grid-cols-1 gap-2 px-1">
  {users.map((associate) => (
    <div
      key={associate.u_id}
      className="bg-white rounded-lg px-3 py-3 relative cursor-pointer hover:shadow-lg transition-shadow w-full"
      style={{
        borderLeft: `6px solid ${getUserColor(associate.u_id)}40`,
        borderRight: '1px solid rgba(0, 174, 247, 0.25)',
        borderTop: '1px solid rgba(0, 174, 247, 0.25)',
        borderBottom: '2px solid rgba(0, 174, 247, 0.25)',
        boxShadow: '0 2px 4px -1px rgba(0, 174, 247, 0.15), inset 0 -6px 8px -6px rgba(0, 174, 247, 0.12)',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
      }}
      onClick={() => handleUserClick(associate)}
    >
      <div className="flex flex-col items-center space-y-1.5">
        <div className="relative">
          <img
            src={
              associate.u_profile_picture
                ? `${process.env.REACT_APP_API_URL}${associate.u_profile_picture}`
                : '/default-avatar.png'
            }
            alt={`${associate.u_first_name} ${associate.u_last_name}`}
            className="w-20 h-20 rounded-full object-cover"
          />
          {/* Slice 3: hide entirely when u_customer_focus is unset. */}
          {associate.u_customer_focus?.trim() && (
            <div className="absolute -bottom-2 -right-11">
              <div className="relative group">
                <div className="text-sm font-bold bg-[#00aef7]/10 px-2 py-0.5 rounded-full border border-[#00aef7]/20 text-[#00aef7] cursor-help">
                  {associate.u_customer_focus}
                </div>
                <div className="hidden group-hover:block fixed z-50 bg-black text-white text-xs p-2 rounded-md w-64 -translate-x-1/2 left-1/2 mt-2">
                  {associate.tooltip_text}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="text-center space-y-1">
          <h3 className="font-semibold leading-tight">
            {associate.u_first_name || associate.u_last_name
              ? `${associate.u_first_name || ''} ${associate.u_last_name || ''}`.trim()
              : 'Associate Name'}
          </h3>
          <p className="text-sm text-gray-600 leading-snug">
            {associate.u_job_title || 'Job Title'}
          </p>
          <p className="text-xs text-gray-500">
            <a href={`tel:${associate.u_phone_number || ''}`} className="hover:text-blue-600">
              {associate.u_phone_number || 'Mobile Number'}
            </a>
          </p>
          <div className="text-xs text-gray-500">
            <a href={`mailto:${associate.u_email}`} className="hover:text-blue-600">
              {associate.u_email}
            </a>
          </div>
        </div>
      </div>
    </div>
  ))}

      {/* Add New Associate Card */}
      <div
        className="bg-white rounded-lg p-4 pt-3 relative cursor-pointer hover:shadow-lg transition-shadow flex flex-col items-center h-[172px] justify-center"
        style={{
          border: '1px solid rgba(0, 174, 247, 0.25)',
          borderBottomWidth: '2px',
          boxShadow: '0 2px 4px -1px rgba(0, 174, 247, 0.15), inset 0 -6px 8px -6px rgba(0, 174, 247, 0.12)'
        }}
        onClick={() => setIsAddAssociateModalOpen(true)}
      >
        <div className="flex flex-col items-center">
          <div className="relative">
            <img
              src="/images/default-avatar.png"
              alt="Add Associate"
              className="w-20 h-20 rounded-full object-cover opacity-30"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <svg className="w-16 h-16 text-[#00aef7]" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </div>
          <p className="text-sm font-semibold text-[#00aef7] mt-2">Add Associate</p>
        </div>
      </div>
    </div>
  </div>
</div>


        <div className="w-full md:w-[28%] bg-white rounded-lg p-4 h-[calc(100vh-7rem)] shadow-[0_0_20px_rgba(0,0,150,0.1)] flex flex-col">
          <div className="top-0 bg-white pb-2 z-10">
            <h2 className="text-xl font-bold text-[#00aef7] mb-2 text-center">
              Ratings & Reviews
            </h2>
          </div>

          <div
            className="relative flex-1 min-h-0 overflow-y-auto pl-3 pr-2 rounded-lg scrollbar-thin scrollbar-thumb-gray-100 scrollbar-track-transparent"
            onScroll={handleReviewsScroll}
          >
            {reviewsLoaded && (
              <AllReviewsList
                reviews={reviews.slice(0, visibleCount)}
                showReplyStatus={true}   // Show existing replies
                hideControls={true}      // Hide "Not Replied" links
                getUserColor={getUserColor}  // <-- pass color resolver
              />
            )}

          </div>
        </div>

      </div>
      {isAddAssociateModalOpen && (
        <AddAssociateModal
          isOpen={true}
          onClose={() => setIsAddAssociateModalOpen(false)}
          businessId={company.b_id}
        />
      )}
    </div>
  );
};

export default CompanyProfile;